// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@conklin/App/Components/Common/XngZone";
import { CustomAppState } from "@conklin/App/Store/Reducers";
import TabGroup, { TabGroupPresentationProps } from "@conklin/MobiusOverrides/TabGroup/TabGroup";
import { XNGAGE_CUSTOM_WIDGET } from "@conklin/XDL/constants";
import { SecondaryAccordionProps } from "@conklin/XDL/PreStyle/accordion";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Tab from "@insite/mobius/Tab/Tab";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";

interface TabsStyles {
    tabsContainer?: TabGroupPresentationProps;
    wrapper?: InjectableCss;
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    accordion?: AccordionPresentationProps;
}

export const tabsStyles: TabsStyles = {};

const styles = tabsStyles;

const enum tabFields {
    tabName = "tabName",
}

const enum fields {
    title = "title",
    tabs = "tabs",
    buttonTitle = "buttonTitle",
    buttonLink = "buttonLink",
}

interface TabField {
    fields: {
        [tabFields.tabName]: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.tabs]: TabField[];
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonTitle]: string;
    };
}
type Props = OwnProps;

const XTabs: FC<Props> = ({ fields: { tabs = [], buttonLink, buttonTitle, title }, id }) => {
    const isEditing = useShellContext().isEditing;
    const tabContainerRef = useRef<HTMLDivElement>(null);
    const [tabsState, setTabsState] = useState<TabField[]>([]);
    const { _tabsState, isTabletScreen } = useAppSelector(state => ({
        _tabsState: (state as CustomAppState).xTabs,
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
    }));
    useEffect(() => {
        setTabsState(tabs);
    }, [isEditing, tabs]);
    const isVisibleTab = (tab: TabField) => {
        const tabPanel = tabContainerRef.current?.querySelector(`[aria-labelledby="${tab.fields.tabName}"]`); // handle non-loading zones
        return !!_tabsState[`tab_${tab.fields.tabName}`] || !!tabPanel?.children?.[0]?.children?.length;
    };

    const atLeastOneVisible = useMemo(() => !!tabsState.find(tab => isVisibleTab(tab)), [_tabsState]);

    return (
        <StyledWrapper {...styles.wrapper} ref={tabContainerRef} key={`tabsWrapper_${tabsState.length}`}>
            {!!tabsState.length && !isTabletScreen && (
                <StyledWrapper {...styles.container}>
                    {atLeastOneVisible && !!title && <Typography {...styles.title}>{title}</Typography>}
                    <TabGroup {...styles.tabsContainer}>
                        {tabsState.slice(0, 5).map(tab => (
                            <Tab
                                key={`${tab.fields.tabName}`}
                                headline={tab.fields.tabName}
                                tabKey={tab.fields.tabName}
                                aria-label={`${tab.fields.tabName} tab`}
                                hidden={!isEditing && !isVisibleTab(tab)}
                            >
                                <XngZone
                                    contentId={id}
                                    zoneName={`tab_${tab.fields.tabName}`}
                                    hint={`Zone for ${tab.fields.tabName}`}
                                />
                            </Tab>
                        ))}
                    </TabGroup>
                </StyledWrapper>
            )}
            {!!tabsState.length && isTabletScreen && (
                <Accordion headingLevel={4} {...styles.accordion}>
                    {tabsState.slice(0, 5).map((tab, index) => (
                        <ManagedAccordionSection
                            key={tab.fields.tabName}
                            title={tab.fields.tabName}
                            aria-label={`${tab.fields.tabName} tab`}
                            initialExpanded={index === 0}
                            {...SecondaryAccordionProps}
                        >
                            <XngZone
                                contentId={id}
                                zoneName={`tab_${tab.fields.tabName}`}
                                hint={`Zone for ${tab.fields.tabName}`}
                            />
                        </ManagedAccordionSection>
                    ))}
                </Accordion>
            )}
        </StyledWrapper>
    );
};
const xTabWidget: WidgetModule = {
    component: XTabs,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET as any,
        fieldDefinitions: [
            {
                editorTemplate: "ListField",
                name: fields.tabs,
                getDisplay: (item: any) => item.fields.tabName,
                defaultValue: [],
                fieldType: "General",
                isRequired: true,
                fieldDefinitions: [
                    {
                        name: tabFields.tabName,
                        editorTemplate: "TextField",
                        displayName: "Tab Name",
                        isRequired: true,
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};
export default xTabWidget;
